// Inpaint-wrapper. All OpenCV-körning sker i en Web Worker (se
// opencv-loader.ts) så main thread aldrig blockeras.

import { inpaintViaWorker } from './opencv-loader';

export interface InpaintOptions {
  inpaintRadius?: number;
  algorithm?: 'TELEA' | 'NS';
}

export interface InpaintResult {
  canvas: HTMLCanvasElement;
  elapsedMs: number;
}

/**
 * Inpainta `source` med hjälp av `mask`. Returnerar en ny canvas.
 *
 * `mask` ska ha samma storlek som `source`. Vita pixlar (>=128) inpaintas;
 * svarta pixlar (<128) lämnas orörda.
 *
 * All OpenCV-körning sker i Web Worker — main thread förblir responsiv.
 */
export async function inpaintCanvas(
  source: HTMLCanvasElement,
  mask: HTMLCanvasElement,
  opts: InpaintOptions = {},
): Promise<InpaintResult> {
  if (source.width !== mask.width || source.height !== mask.height) {
    throw new Error(
      `Source and mask must be same size (got ${source.width}x${source.height} vs ${mask.width}x${mask.height})`,
    );
  }

  // Hämta ImageData från canvas:erna (hänt på main thread, men snabbt)
  const srcCtx = source.getContext('2d', { willReadFrequently: true });
  const maskCtx = mask.getContext('2d', { willReadFrequently: true });
  if (!srcCtx || !maskCtx) {
    throw new Error('Could not get 2D context from canvas');
  }
  const imageData = srcCtx.getImageData(0, 0, source.width, source.height);
  const maskData = maskCtx.getImageData(0, 0, mask.width, mask.height);

  const { resultData, elapsedMs } = await inpaintViaWorker({
    imageData,
    maskData,
    inpaintRadius: opts.inpaintRadius ?? 3,
    algorithm: opts.algorithm,
  });

  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const outCtx = out.getContext('2d');
  if (!outCtx) throw new Error('Could not get 2D context for output canvas');
  outCtx.putImageData(resultData, 0, 0);

  return { canvas: out, elapsedMs };
}
